import React, { useState, useEffect } from 'react';
import { Shield, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'edrano_cookie_consent';

const CATEGORIES = [
  {
    id: 'necessary',
    label: 'Strictly Necessary',
    description: 'Required for the website to function. Cannot be disabled.',
    locked: true,
  },
  {
    id: 'analytics',
    label: 'Analytics',
    description: 'Help us understand how visitors interact with the website by collecting anonymous usage data.',
    locked: false,
  },
  {
    id: 'marketing',
    label: 'Marketing',
    description: 'Used to deliver relevant advertisements and track campaign performance across platforms.',
    locked: false,
  },
];

function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(prefs) {
  const record = { ...prefs, timestamp: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  window.dispatchEvent(new CustomEvent('cookie-consent-update', { detail: record }));
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setPrefs({ necessary: true, analytics: !!existing.analytics, marketing: !!existing.marketing });
    }

    const handleReset = () => {
      setVisible(true);
      setExpanded(false);
    };
    window.addEventListener('cookie-consent-reset', handleReset);
    return () => window.removeEventListener('cookie-consent-reset', handleReset);
  }, []);

  const accept = (all) => {
    const p = all
      ? { necessary: true, analytics: true, marketing: true }
      : prefs;
    saveConsent(p);
    setVisible(false);
  };

  const reject = () => {
    const p = { necessary: true, analytics: false, marketing: false };
    saveConsent(p);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-0 inset-x-0 z-[9999] p-4 sm:p-6"
      data-testid="cookie-banner"
    >
      <div className="max-w-2xl mx-auto bg-stone-950 border border-stone-800 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="px-5 pt-5 pb-3">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-white">We value your privacy</h3>
              <p className="text-xs text-stone-400 mt-1 leading-relaxed">
                We use cookies to enhance your experience, analyze traffic, and personalize content. 
                You can manage your preferences below or learn more in our{' '}
                <Link to="/cookies" className="text-amber-500 hover:underline">Cookie Policy</Link>
                {' '}and{' '}
                <Link to="/privacy" className="text-amber-500 hover:underline">Privacy Policy</Link>.
              </p>
            </div>
          </div>
        </div>

        {/* Expandable categories */}
        <div className="px-5">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-white transition-colors py-2"
            data-testid="cookie-customize-toggle"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            Customize preferences
          </button>

          {expanded && (
            <div className="space-y-2.5 pb-3" data-testid="cookie-categories">
              {CATEGORIES.map((cat) => (
                <label
                  key={cat.id}
                  className="flex items-start gap-3 p-3 rounded-lg bg-stone-900/60 border border-stone-800 cursor-pointer"
                  data-testid={`cookie-cat-${cat.id}`}
                >
                  <input
                    type="checkbox"
                    checked={prefs[cat.id]}
                    disabled={cat.locked}
                    onChange={(e) =>
                      !cat.locked && setPrefs((p) => ({ ...p, [cat.id]: e.target.checked }))
                    }
                    className="mt-0.5 accent-amber-500 w-4 h-4"
                    data-testid={`cookie-toggle-${cat.id}`}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-white">{cat.label}</span>
                      {cat.locked && (
                        <span className="text-[10px] text-stone-500 bg-stone-800 px-1.5 py-0.5 rounded">Always on</span>
                      )}
                    </div>
                    <p className="text-[11px] text-stone-500 mt-0.5 leading-relaxed">{cat.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 px-5 pb-5 pt-1">
          <button
            onClick={reject}
            className="flex-1 text-xs font-medium text-stone-400 hover:text-white border border-stone-700 hover:border-stone-500 rounded-lg py-2.5 transition-colors"
            data-testid="cookie-reject-btn"
          >
            Reject All
          </button>
          {expanded && (
            <button
              onClick={() => accept(false)}
              className="flex-1 text-xs font-medium text-stone-300 bg-stone-800 hover:bg-stone-700 rounded-lg py-2.5 transition-colors"
              data-testid="cookie-save-btn"
            >
              Save Preferences
            </button>
          )}
          <button
            onClick={() => accept(true)}
            className="flex-1 text-xs font-medium text-stone-950 bg-amber-500 hover:bg-amber-400 rounded-lg py-2.5 transition-colors"
            data-testid="cookie-accept-btn"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
